/* eslint react/prop-types: 0 */
import { Suspense, useCallback, useState } from 'react';
import { lazily } from 'react-lazily';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@hotjar/error-handler';
import { PageLoader } from '@hotjar/webapp/components/PageLoader/PageLoader';
import { useWindowTitle } from '@hotjar/webapp/hooks/useWindowTitle';
import { useCurrentUser } from '@hotjar/user';
import { useSites } from '@hotjar/site';
import { useSearchParams } from 'react-router-dom';

// NOTE: using React.lazy and Suspense is not a common practice.
// We use lazily lib to be able to import component without a default export.
const { UserAttributesPage } = lazily(() => import('./UserAttributesPage'));

export function AsyncUserAttributesPage(): JSX.Element | null {
  const { t } = useTranslation();
  useWindowTitle(t('common:user-attributes.title'));
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const {
    query: { data: sites, isLoading: isLoadingSites, isPlaceholderData: isPlaceholderDataSites },
  } = useSites();
  const [searchParams, setSearchParams] = useSearchParams();
  const [siteId, setSiteId] = useState<number | undefined>(() => {
    const siteIdParam = searchParams.get('site-id');
    return siteIdParam ? +siteIdParam : undefined;
  });

  const handleSiteChange = useCallback(
    (newSiteId: number | undefined) => {
      if (newSiteId && newSiteId !== siteId) {
        setSiteId(newSiteId);
        setSearchParams({ 'site-id': `${newSiteId}` });
      }
    },
    [setSearchParams, siteId],
  );

  const isLoading = isLoadingCurrentUser || isLoadingSites || isPlaceholderDataSites;

  return (
    <ErrorBoundary squadSentryTag="product-foundations" featureName="setting.userattributes">
      <Suspense fallback={<PageLoader />}>
        {isLoading && <PageLoader />}
        {!isLoading && currentUser && (
          <UserAttributesPage
            availableSites={sites}
            currentUser={currentUser}
            siteId={siteId}
            setSiteId={handleSiteChange}
          />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}
